import { useContext } from 'react';
import { JobsStateContext, JobsDispatchContext } from '../context/JobsContextObject';
import type { JobsState, JobsActions } from '../types';

/**
 * Hook to access job state only (read operations).
 * Following Kent C. Dodds' pattern, this prevents unnecessary re-renders
 * when components only need to read state.
 * 
 * @throws {Error} If used outside of JobsDataProvider
 * @returns {JobsState} The current job state
 * 
 * @example
 * const { jobs, loading, error } = useJobsState();
 */
const useJobsState = (): JobsState => {
  const context = useContext(JobsStateContext);
  if (context === undefined) {
    throw new Error('useJobsState must be used within a JobsDataProvider');
  }
  return context;
};

/**
 * Hook to access job actions only (write operations).
 * Following Kent C. Dodds' pattern, this prevents unnecessary re-renders
 * when components only need to perform actions.
 * 
 * @throws {Error} If used outside of JobsDataProvider
 * @returns {JobsActions} The job action functions
 * 
 * @example
 * const { addJob, updateJob, deleteJob } = useJobsActions();
 */
const useJobsActions = (): JobsActions => {
  const context = useContext(JobsDispatchContext);
  if (context === undefined) {
    throw new Error('useJobsActions must be used within a JobsDataProvider');
  }
  return context;
};

/**
 * Convenience hook to access both state and actions.
 * Use this when a component needs both read and write access.
 * For better performance, prefer using useJobsState() or useJobsActions()
 * separately when possible.
 * 
 * @throws {Error} If used outside of JobsDataProvider
 * @returns {JobsState & JobsActions} Combined state and actions
 * 
 * @example
 * const { jobs, loading, addJob, updateJob } = useJobs();
 */
export const useJobs = (): JobsState & JobsActions => {
  return {
    ...useJobsState(),
    ...useJobsActions(),
  };
};
